import { Check, Info, X, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useToasts } from '@/store/toast';
import { useT } from '@/i18n';
import type { ToastTone } from '@/store/toast';

const ICONS: Record<ToastTone, typeof Info> = {
  neutral: Info,
  success: Check,
  error: AlertCircle,
};

const TONES: Record<ToastTone, string> = {
  neutral: 'border-hairline-strong text-ink',
  success: 'border-accent-graphic/45 text-ink',
  error: 'border-danger/45 text-ink',
};

const ICON_TONES: Record<ToastTone, string> = {
  neutral: 'text-muted',
  success: 'text-accent-graphic',
  error: 'text-danger',
};

export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);
  const t = useT();

  return (
    <div
      // Polite so a toast never interrupts the viewer mid-playback.
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6"
    >
      {toasts.map((item) => {
        const Icon = ICONS[item.tone];
        return (
          <div
            key={item.id}
            className={cn(
              'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-md border',
              'bg-surface-raised px-4 py-3 text-sm animate-fade-up',
              TONES[item.tone],
            )}
          >
            <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', ICON_TONES[item.tone])} />
            <p className="flex-1">{item.message}</p>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              aria-label={t('common.close')}
              className="-m-1 rounded-xs p-1 text-muted transition-colors hover:text-ink"
            >
              <X className="size-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
